import { useAppLanguage } from '@/hooks/useAppLanguage';
import Colors from '@/constants/colors';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

const content = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: December 2024',
    intro: {
      title: 'Introduction',
      text: 'Welcome to Umland. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we handle information when you use our application.',
    },
    dataCollection: {
      title: 'Data Collection',
      text: 'We do NOT collect, store, or transmit any personal data to external servers. Umland is designed with privacy as a core principle. We do not:',
      items: [
        'Collect your name, email, or contact information',
        'Track your location or movements',
        'Store your browsing history on our servers',
        'Share any data with third parties',
        'Use analytics or tracking tools',
        'Display targeted advertisements',
      ],
    },
    localStorage: {
      title: 'Local Storage',
      text: 'The app stores some preferences locally on your device (not on our servers) to improve your experience:',
      items: [
        'Language preference (English, Russian, or Polish)',
        'Default currency for the currency widget',
        'Your favorites (saved articles and places)',
        'Widget settings (which widgets are enabled and their order)',
      ],
      note: 'This data never leaves your device and is stored only in your browser\'s local storage. You can clear this data at any time by clearing your browser data or uninstalling the app.',
    },
    thirdParty: {
      title: 'Third-Party Services',
      text: 'The app fetches data from external APIs to provide certain features:',
      items: [
        'Weather data: We request weather information for Reykjavik. No personal data is sent with these requests.',
        'Currency rates: We fetch exchange rates from public APIs. No personal data is sent.',
      ],
      note: 'These requests do not include any personally identifiable information.',
    },
    cookies: {
      title: 'Cookies',
      text: 'We do not use cookies or any similar tracking technologies.',
    },
    gdpr: {
      title: 'Your Rights (GDPR)',
      text: 'Under the General Data Protection Regulation (GDPR), you have the following rights:',
      items: [
        'Right to access - you can view all your data directly in the app settings',
        'Right to erasure - you can delete all local data by clearing browser data',
        'Right to portability - your preferences are stored locally and can be exported',
        'Right to restriction - you control all data stored locally',
      ],
      note: 'Since we do not collect or store personal data on our servers, most GDPR provisions regarding data controllers do not apply to our service.',
    },
    children: {
      title: 'Children\'s Privacy',
      text: 'Our app does not knowingly collect any personal information from children under 13 years of age. Since we do not collect personal data from any users, this extends to children as well.',
    },
    changes: {
      title: 'Changes to This Policy',
      text: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.',
    },
    contact: {
      title: 'Contact Us',
      text: 'If you have any questions about this privacy policy, please contact us at:',
      email: 'support@umland.is',
    },
  },
  ru: {
    title: 'Политика конфиденциальности',
    lastUpdated: 'Последнее обновление: декабрь 2024',
    intro: {
      title: 'Введение',
      text: 'Добро пожаловать в Umland. Мы уважаем вашу конфиденциальность и стремимся защитить ваши персональные данные. Эта политика объясняет, как мы обрабатываем информацию при использовании нашего приложения.',
    },
    dataCollection: {
      title: 'Сбор данных',
      text: 'Мы НЕ собираем, не храним и не передаем персональные данные на внешние серверы. Umland разработан с конфиденциальностью как основным принципом. Мы не:',
      items: [
        'Собираем ваше имя, email или контактную информацию',
        'Отслеживаем ваше местоположение или перемещения',
        'Храним вашу историю просмотров на наших серверах',
        'Передаем данные третьим лицам',
        'Используем аналитику или инструменты отслеживания',
        'Показываем таргетированную рекламу',
      ],
    },
    localStorage: {
      title: 'Локальное хранилище',
      text: 'Приложение сохраняет некоторые настройки локально на вашем устройстве (не на наших серверах) для улучшения опыта использования:',
      items: [
        'Языковые настройки (английский, русский или польский)',
        'Валюта по умолчанию для виджета валют',
        'Ваше избранное (сохраненные статьи и места)',
        'Настройки виджетов (какие виджеты включены и их порядок)',
      ],
      note: 'Эти данные никогда не покидают ваше устройство и хранятся только в локальном хранилище браузера. Вы можете удалить эти данные в любое время, очистив данные браузера или удалив приложение.',
    },
    thirdParty: {
      title: 'Сторонние сервисы',
      text: 'Приложение получает данные из внешних API для предоставления определенных функций:',
      items: [
        'Данные о погоде: мы запрашиваем информацию о погоде для Рейкьявика. Никакие персональные данные не отправляются с этими запросами.',
        'Курсы валют: мы получаем курсы обмена из публичных API. Никакие персональные данные не отправляются.',
      ],
      note: 'Эти запросы не включают никакой персонально идентифицируемой информации.',
    },
    cookies: {
      title: 'Cookies',
      text: 'Мы не используем cookies или подобные технологии отслеживания.',
    },
    gdpr: {
      title: 'Ваши права (GDPR)',
      text: 'В соответствии с Общим регламентом защиты данных (GDPR), вы имеете следующие права:',
      items: [
        'Право на доступ - вы можете просмотреть все свои данные прямо в настройках приложения',
        'Право на удаление - вы можете удалить все локальные данные, очистив данные браузера',
        'Право на переносимость - ваши настройки хранятся локально и могут быть экспортированы',
        'Право на ограничение - вы контролируете все данные, хранящиеся локально',
      ],
      note: 'Поскольку мы не собираем и не храним персональные данные на наших серверах, большинство положений GDPR относительно контролеров данных не применимы к нашему сервису.',
    },
    children: {
      title: 'Конфиденциальность детей',
      text: 'Наше приложение сознательно не собирает персональную информацию от детей младше 13 лет. Поскольку мы не собираем персональные данные ни от каких пользователей, это распространяется и на детей.',
    },
    changes: {
      title: 'Изменения в этой политике',
      text: 'Мы можем время от времени обновлять эту политику конфиденциальности. Мы уведомим вас о любых изменениях, опубликовав новую политику на этой странице и обновив дату "Последнее обновление".',
    },
    contact: {
      title: 'Связаться с нами',
      text: 'Если у вас есть вопросы об этой политике конфиденциальности, пожалуйста, свяжитесь с нами:',
      email: 'support@umland.is',
    },
  },
  pl: {
    title: 'Polityka prywatności',
    lastUpdated: 'Ostatnia aktualizacja: grudzień 2024',
    intro: {
      title: 'Wprowadzenie',
      text: 'Witamy w Umland. Szanujemy Twoją prywatność i zobowiązujemy się do ochrony Twoich danych osobowych. Niniejsza polityka prywatności wyjaśnia, jak postępujemy z informacjami podczas korzystania z naszej aplikacji.',
    },
    dataCollection: {
      title: 'Zbieranie danych',
      text: 'NIE zbieramy, nie przechowujemy ani nie przesyłamy żadnych danych osobowych na zewnętrzne serwery. Umland został zaprojektowany z prywatnością jako podstawową zasadą. Nie:',
      items: [
        'Zbieramy Twojego imienia, emaila ani danych kontaktowych',
        'Śledzimy Twojej lokalizacji ani ruchów',
        'Przechowujemy Twojej historii przeglądania na naszych serwerach',
        'Udostępniamy żadnych danych stronom trzecim',
        'Używamy narzędzi analitycznych ani śledzących',
        'Wyświetlamy ukierunkowanych reklam',
      ],
    },
    localStorage: {
      title: 'Lokalna pamięć',
      text: 'Aplikacja przechowuje niektóre preferencje lokalnie na Twoim urządzeniu (nie na naszych serwerach) aby poprawić doświadczenie użytkownika:',
      items: [
        'Preferencje językowe (angielski, rosyjski lub polski)',
        'Domyślna waluta dla widżetu walut',
        'Twoje ulubione (zapisane artykuły i miejsca)',
        'Ustawienia widżetów (które widżety są włączone i ich kolejność)',
      ],
      note: 'Te dane nigdy nie opuszczają Twojego urządzenia i są przechowywane tylko w lokalnej pamięci przeglądarki. Możesz usunąć te dane w dowolnym momencie, czyszcząc dane przeglądarki lub odinstalowując aplikację.',
    },
    thirdParty: {
      title: 'Usługi stron trzecich',
      text: 'Aplikacja pobiera dane z zewnętrznych API aby zapewnić niektóre funkcje:',
      items: [
        'Dane pogodowe: prosimy o informacje pogodowe dla Reykjaviku. Żadne dane osobowe nie są wysyłane z tymi zapytaniami.',
        'Kursy walut: pobieramy kursy wymiany z publicznych API. Żadne dane osobowe nie są wysyłane.',
      ],
      note: 'Te zapytania nie zawierają żadnych informacji umożliwiających identyfikację osoby.',
    },
    cookies: {
      title: 'Cookies',
      text: 'Nie używamy plików cookies ani podobnych technologii śledzenia.',
    },
    gdpr: {
      title: 'Twoje prawa (RODO)',
      text: 'Zgodnie z Ogólnym Rozporządzeniem o Ochronie Danych (RODO), masz następujące prawa:',
      items: [
        'Prawo dostępu - możesz przeglądać wszystkie swoje dane bezpośrednio w ustawieniach aplikacji',
        'Prawo do usunięcia - możesz usunąć wszystkie lokalne dane, czyszcząc dane przeglądarki',
        'Prawo do przenoszenia - Twoje preferencje są przechowywane lokalnie i mogą być eksportowane',
        'Prawo do ograniczenia - kontrolujesz wszystkie dane przechowywane lokalnie',
      ],
      note: 'Ponieważ nie zbieramy ani nie przechowujemy danych osobowych na naszych serwerach, większość przepisów RODO dotyczących administratorów danych nie ma zastosowania do naszej usługi.',
    },
    children: {
      title: 'Prywatność dzieci',
      text: 'Nasza aplikacja świadomie nie zbiera żadnych danych osobowych od dzieci poniżej 13 roku życia. Ponieważ nie zbieramy danych osobowych od żadnych użytkowników, dotyczy to również dzieci.',
    },
    changes: {
      title: 'Zmiany w tej polityce',
      text: 'Możemy od czasu do czasu aktualizować tę politykę prywatności. Powiadomimy Cię o wszelkich zmianach, publikując nową politykę na tej stronie i aktualizując datę "Ostatnia aktualizacja".',
    },
    contact: {
      title: 'Skontaktuj się z nami',
      text: 'Jeśli masz pytania dotyczące tej polityki prywatności, skontaktuj się z nami:',
      email: 'support@umland.is',
    },
  },
};

export default function PrivacyPolicyPage() {
  const { language } = useAppLanguage();
  const t = content[language] || content.en;

  return (
    <DetailPageLayout>
      <DetailPageHeader title={t.title} />
      <div style={styles.content}>
        <p style={styles.lastUpdated}>{t.lastUpdated}</p>

        {/* Introduction */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.intro.title}</h2>
          <p style={styles.bodyText}>{t.intro.text}</p>
        </section>

        {/* Data Collection */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.dataCollection.title}</h2>
          <p style={styles.bodyText}>{t.dataCollection.text}</p>
          <ul style={styles.list}>
            {t.dataCollection.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Local Storage */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.localStorage.title}</h2>
          <p style={styles.bodyText}>{t.localStorage.text}</p>
          <ul style={styles.list}>
            {t.localStorage.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.note}>{t.localStorage.note}</p>
        </section>

        {/* Third-Party Services */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.thirdParty.title}</h2>
          <p style={styles.bodyText}>{t.thirdParty.text}</p>
          <ul style={styles.list}>
            {t.thirdParty.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.note}>{t.thirdParty.note}</p>
        </section>

        {/* Cookies */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.cookies.title}</h2>
          <p style={styles.bodyText}>{t.cookies.text}</p>
        </section>

        {/* GDPR Rights */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.gdpr.title}</h2>
          <p style={styles.bodyText}>{t.gdpr.text}</p>
          <ul style={styles.list}>
            {t.gdpr.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.note}>{t.gdpr.note}</p>
        </section>

        {/* Children's Privacy */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.children.title}</h2>
          <p style={styles.bodyText}>{t.children.text}</p>
        </section>

        {/* Changes */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.changes.title}</h2>
          <p style={styles.bodyText}>{t.changes.text}</p>
        </section>

        {/* Contact */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.contact.title}</h2>
          <p style={styles.bodyText}>{t.contact.text}</p>
          <a href={`mailto:${t.contact.email}`} style={styles.emailLink}>
            {t.contact.email}
          </a>
        </section>
      </div>
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    backgroundColor: Colors.background,
    minHeight: '100vh',
    padding: '24px',
  } as React.CSSProperties,

  lastUpdated: {
    fontSize: '14px',
    color: Colors.textSecondary,
    marginBottom: '24px',
  } as React.CSSProperties,

  section: {
    marginBottom: '28px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '12px',
  } as React.CSSProperties,

  bodyText: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '24px',
    margin: 0,
  } as React.CSSProperties,

  list: {
    margin: '12px 0 0 0',
    paddingLeft: '20px',
  } as React.CSSProperties,

  listItem: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '28px',
  } as React.CSSProperties,

  note: {
    fontSize: '14px',
    color: Colors.textSecondary,
    lineHeight: '22px',
    marginTop: '12px',
    fontStyle: 'italic',
  } as React.CSSProperties,

  emailLink: {
    display: 'inline-block',
    marginTop: '8px',
    fontSize: '16px',
    color: Colors.primary,
    textDecoration: 'none',
    fontWeight: '500',
  } as React.CSSProperties,
};
